import type { BookChapter } from '@/components/books/book';
import type { BookCardBook } from '@/components/books/book-card';
import { headingOutlineStructurallyEqual, type HeadingOutlineItem } from '@/nav/heading-outline';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

export type ScrollToHeadingFn = (headingId: string | null, index: number) => void;

export type DemoNavRegistration = {
    key: string;
    demoId: string;
    headings: HeadingOutlineItem[];
    scrollToHeading: ScrollToHeadingFn;
};

export type BookNavRegistration = {
    key: string;
    book: BookCardBook;
    chapters: BookChapter[];
    activeSectionId: string | null;
    headings: HeadingOutlineItem[];
    scrollToHeading: ScrollToHeadingFn | null;
};

type PlatformSidebarOutletContextValue = {
    demoRegistration: DemoNavRegistration | null;
    registerDemoNav: (reg: DemoNavRegistration) => void;
    clearDemoNav: (registrationKey: string) => void;
    resetDemoNav: () => void;
    bookRegistration: BookNavRegistration | null;
    registerBookNav: (reg: BookNavRegistration) => void;
    updateBookNavHeadings: (registrationKey: string, headings: HeadingOutlineItem[], scrollToHeading: ScrollToHeadingFn | null) => void;
    clearBookNav: (registrationKey: string) => void;
};

const PlatformSidebarOutletContext = createContext<PlatformSidebarOutletContextValue | null>(null);

export function PlatformSidebarOutletProvider({ children }: { children: ReactNode }) {
    const [demoRegistration, setDemoRegistration] = useState<DemoNavRegistration | null>(null);
    const demoActiveKeyRef = useRef<string | null>(null);
    const latestDemoScrollToHeadingRef = useRef<ScrollToHeadingFn | null>(null);

    const [bookRegistration, setBookRegistration] = useState<BookNavRegistration | null>(null);
    const bookActiveKeyRef = useRef<string | null>(null);
    const latestBookScrollToHeadingRef = useRef<ScrollToHeadingFn | null>(null);

    const stableDemoScrollToHeading = useCallback<ScrollToHeadingFn>((headingId, index) => {
        latestDemoScrollToHeadingRef.current?.(headingId, index);
    }, []);

    const stableBookScrollToHeading = useCallback<ScrollToHeadingFn>((headingId, index) => {
        latestBookScrollToHeadingRef.current?.(headingId, index);
    }, []);

    const registerDemoNav = useCallback(
        (reg: DemoNavRegistration) => {
            demoActiveKeyRef.current = reg.key;
            latestDemoScrollToHeadingRef.current = reg.scrollToHeading;

            setDemoRegistration(prev => {
                if (
                    prev &&
                    prev.key === reg.key &&
                    prev.demoId === reg.demoId &&
                    headingOutlineStructurallyEqual(prev.headings, reg.headings)
                ) {
                    return prev;
                }
                return {
                    ...reg,
                    scrollToHeading: stableDemoScrollToHeading,
                };
            });
        },
        [stableDemoScrollToHeading],
    );

    const clearDemoNav = useCallback((registrationKey: string) => {
        if (demoActiveKeyRef.current !== registrationKey) return;
        demoActiveKeyRef.current = null;
        latestDemoScrollToHeadingRef.current = null;
        setDemoRegistration(null);
    }, []);

    const resetDemoNav = useCallback(() => {
        demoActiveKeyRef.current = null;
        latestDemoScrollToHeadingRef.current = null;
        setDemoRegistration(null);
    }, []);

    const registerBookNav = useCallback(
        (reg: BookNavRegistration) => {
            bookActiveKeyRef.current = reg.key;
            latestBookScrollToHeadingRef.current = reg.scrollToHeading;

            setBookRegistration(prev => {
                if (prev && prev.key === reg.key && prev.book.id === reg.book.id && prev.activeSectionId === reg.activeSectionId) {
                    return {
                        ...reg,
                        headings: prev.headings,
                        scrollToHeading: prev.scrollToHeading,
                    };
                }
                return {
                    ...reg,
                    scrollToHeading: reg.scrollToHeading ? stableBookScrollToHeading : null,
                };
            });
        },
        [stableBookScrollToHeading],
    );

    const updateBookNavHeadings = useCallback(
        (registrationKey: string, headings: HeadingOutlineItem[], scrollToHeading: ScrollToHeadingFn | null) => {
            if (bookActiveKeyRef.current !== registrationKey) return;

            latestBookScrollToHeadingRef.current = scrollToHeading;

            setBookRegistration(prev => {
                if (!prev) return prev;
                if (
                    headingOutlineStructurallyEqual(prev.headings, headings) &&
                    (scrollToHeading == null) === (prev.scrollToHeading == null)
                ) {
                    return prev;
                }
                return {
                    ...prev,
                    headings,
                    scrollToHeading: scrollToHeading ? stableBookScrollToHeading : null,
                };
            });
        },
        [stableBookScrollToHeading],
    );

    const clearBookNav = useCallback((registrationKey: string) => {
        if (bookActiveKeyRef.current !== registrationKey) return;
        bookActiveKeyRef.current = null;
        latestBookScrollToHeadingRef.current = null;
        setBookRegistration(null);
    }, []);

    const value = useMemo(
        () => ({
            demoRegistration,
            registerDemoNav,
            clearDemoNav,
            resetDemoNav,
            bookRegistration,
            registerBookNav,
            updateBookNavHeadings,
            clearBookNav,
        }),
        [
            demoRegistration,
            registerDemoNav,
            clearDemoNav,
            resetDemoNav,
            bookRegistration,
            registerBookNav,
            updateBookNavHeadings,
            clearBookNav,
        ],
    );

    return <PlatformSidebarOutletContext.Provider value={value}>{children}</PlatformSidebarOutletContext.Provider>;
}

export function usePlatformSidebarOutlet() {
    const ctx = useContext(PlatformSidebarOutletContext);
    if (!ctx) {
        throw new Error('usePlatformSidebarOutlet must be used within PlatformSidebarOutletProvider');
    }
    return ctx;
}
